import {css, StyleSheet} from 'aphrodite';
import moment from 'moment';
import React from 'react';

import {CreateAnalysisParams} from 'analysis-tool/AnalysisDAO';
import CreateAnalysisForm from 'analysis-tool/create-analysis-modal/CreateAnalysisForm';
import {SeedDataConfigType} from 'analysis-tool/create-analysis-modal/SeedDataTypes';
import useCreateAnalysisState from 'analysis-tool/create-analysis-modal/useCreateAnalysisState';
import * as Analytics from 'analytics/Analytics';
import {AnalyticsMetadataForContext} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin, ANALYTICS_MODULE, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {WorksheetAnalyzeButtonQuery} from 'procedures/shared-components/analysis/__generated/WorksheetAnalyzeButtonQuery';
import {
  AccessiblePMEI,
  useAnalyzePreconfiguredProcedureState,
} from 'procedures/shared-components/analysis/useAnalyzePreconfiguredProcedureState';
import {StudySearchItem} from 'studies/StudySearchInput';
import {createAnalysisForStudy} from 'studies/typed/utils/analysis/createAnalysisForStudy';
import {disallowsAdhocAnalysis} from 'studies/TypedStudyUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import useOnMount from 'ui-library/hooks/useOnMount';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import ToggleGroup from 'ui-library/ToggleGroup';
import * as DateUtil from 'util/DateUtil';
import {withLoadingScreen} from 'util/Promises';

import {convertConfigIntoSeedData, makeEmptySeedDataConfig} from './preconfiguredAnalysisUtils';

type AccessibleWorksheet = ExcludeInaccessible<WorksheetAnalyzeButtonQuery['worksheet']>;
type AccessibleStudy = ExcludeInaccessible<AccessibleWorksheet['studies'][number]>;

export interface AnalysisModalAdHocProps {
  onRequestSubmit: () => void;
  onRequestClose: () => void;
  worksheet: AccessibleWorksheet;
  pmei: AccessiblePMEI | null;
}

/** @package exported for testing */
export enum AnalysisTypesAvailable {
  AdHoc = 'AdHoc',
  Preconfigured = 'Preconfigured',
}

// The study that this worksheet is executing is on task->pmei->procedureRun->study. However,
// here we're interested in all associated studies, including those which the user explicitly
// attached in the UI.
function getInitialStudySearchItems(worksheet: AccessibleWorksheet): Array<StudySearchItem> {
  return worksheet.studies
    .filter((node): node is AccessibleStudy => !isInaccessible(node))
    .map((study) => ({
      id: study.id,
      name: study.name,
      humanId: study.humanId,
    }));
}

/** @package exported for testing */
export function tryGetPMEIStudyType(pmei: AccessiblePMEI | null): StudyType | null {
  const study =
    pmei?.procedureRun.study == null || isInaccessible(pmei.procedureRun.study)
      ? null
      : pmei.procedureRun.study;
  return study != null ? study.studySchema.studyType : null;
}

/** @package exported for testing */
export function getProcedureTypeFromNullablePMEI(pmei: AccessiblePMEI | null): ProcedureType | null {
  return pmei?.procedureRun.procedureType ?? null;
}

/** @package exported for testing */
export function getAvailableAnalysisTypes(pmei: AccessiblePMEI | null): {
  availableAnalysisTypes: Array<AnalysisTypesAvailable>;
} {
  const studyType = tryGetPMEIStudyType(pmei);

  const disallowAdhocAnalysis = studyType != null && disallowsAdhocAnalysis(studyType);
  return {
    availableAnalysisTypes: disallowAdhocAnalysis
      ? // Note that all worksheets "can" be analyzed with preconfigured analysis templates. However, at this stage
        // we are not ensuring that any analysis templates have been configured.
        [AnalysisTypesAvailable.Preconfigured]
      : [AnalysisTypesAvailable.AdHoc, AnalysisTypesAvailable.Preconfigured],
  };
}

export default function AnalyzeProcedureModal(props: AnalysisModalAdHocProps): JSX.Element {
  // Technically, this is earlier than the time we send the request to the backend, but for consistency
  // with the analysis name, this is the timestamp the backend will use in the name and contents of the
  // post-run step for analysis creation.
  const sentToAnalysisTimestamp = DateUtil.readableTime(moment(), {showTime: true});
  const analyticsMetadata: AnalyticsMetadataForContext = {
    actionOrigin: ActionOrigin.WORKSHEET,
    modalType: 'create_analysis',
  };
  const analyticsModule = ANALYTICS_MODULE.ANALYSIS_TOOL;

  const initialSeedDataConfig = makeEmptySeedDataConfig(props.worksheet.id, sentToAnalysisTimestamp);
  const initialStudyItems = getInitialStudySearchItems(props.worksheet);
  const {availableAnalysisTypes} = getAvailableAnalysisTypes(props.pmei);
  assert(availableAnalysisTypes.length > 0, 'Expected at least one analysis type to be supported');

  const {analysisTemplateConfig, selectedAnalysisTemplateInfo, analyzePreconfiguredProcedurePane} =
    useAnalyzePreconfiguredProcedureState(props.pmei, props.worksheet.id);
  const {createAnalysisState, setCreateAnalysisState, requestParamsInfo} = useCreateAnalysisState({
    initialSeedDataConfig,
    initialStudyItems,
  });

  const disablePreconfigured =
    !availableAnalysisTypes.includes(AnalysisTypesAvailable.Preconfigured) ||
    !analysisTemplateConfig.hasConfiguredTemplates ||
    !BenchlingConfig.PROCEDURES_ENABLE_ANALYSIS_TEMPLATE_INTEGRATION;
  // Default to preconfigured if it's enabled/templates present; otherwise adhoc
  const defaultAnalysisType =
    availableAnalysisTypes.length === 1
      ? availableAnalysisTypes[0]
      : !disablePreconfigured
        ? AnalysisTypesAvailable.Preconfigured
        : AnalysisTypesAvailable.AdHoc;
  const [analysisType, setAnalysisType] = useStateIfMounted<AnalysisTypesAvailable>(defaultAnalysisType);

  useOnMount(() => {
    Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, analyticsModule, analyticsMetadata);
  });

  const disableSubmit =
    analysisType === AnalysisTypesAvailable.Preconfigured
      ? !analysisTemplateConfig.configModalContext.canSubmit
      : !requestParamsInfo.isComplete;

  const studyType = tryGetPMEIStudyType(props.pmei);
  const procedureType = getProcedureTypeFromNullablePMEI(props.pmei);

  async function handleRequestSubmit(): Promise<void> {
    let createAnalysisParams: CreateAnalysisParams;
    props.onRequestClose();
    if (analysisType === AnalysisTypesAvailable.AdHoc) {
      assert(requestParamsInfo.isComplete, 'Expected create analysis request params to be complete');
      FolderDefaults.set(FolderDefaultsKey.ANALYSIS, requestParamsInfo.requestParams.folderId);
      createAnalysisParams = requestParamsInfo.requestParams;
      await withLoadingScreen(
        createAnalysisForStudy(createAnalysisParams, {
          actionOrigin: ActionOrigin.WORKSHEET,
          studyType,
          procedureType,
          sourceObjectIds: [props.worksheet.id],
        }),
        'Creating analysis...'
      );
    } else {
      assert(
        analysisTemplateConfig.configModalContext.fullConfig != null && selectedAnalysisTemplateInfo != null,
        'Should not be able to submit the analysis template modal without a full config'
      );
      createAnalysisParams = {
        name: `${selectedAnalysisTemplateInfo.name}: ${props.worksheet.name}`,
        folderId: props.worksheet.folder.id,
        seedDataConfig: convertConfigIntoSeedData(
          analysisTemplateConfig.configModalContext.fullConfig,
          props.worksheet.id,
          sentToAnalysisTimestamp,
          analysisTemplateConfig.availablePlateIdsToSummarize
        ),
        studyIds: initialStudyItems.map(({id}) => id),
      };
      // For preconfigured, create analysis with a background toast for the analysis & seed data creation
      await createAnalysisForStudy(
        createAnalysisParams,
        {
          actionOrigin: ActionOrigin.WORKSHEET,
          studyType,
          procedureType,
          sourceObjectIds: [props.worksheet.id],
        },
        true
      );
    }
    // The onSubmit callback refetches worksheet to update steps
    props.onRequestSubmit();
  }

  const analysisTypeToggle = (
    <div
      {...getTestProps({component: 'AnalyzeProcedureModal', element: 'analysisOption'})}
      className={css(styles.analysisTypeToggleButtons)}
    >
      <ToggleGroup
        selection={analysisType}
        onChangeSelection={(value: AnalysisTypesAvailable) => {
          setAnalysisType(value);
        }}
        size={ButtonSize.MEDIUM}
        isFullWidth={true}
        options={[
          {
            id: AnalysisTypesAvailable.Preconfigured,
            buttonProps: {text: 'Use template'},
            disabled: !analysisTemplateConfig.hasConfiguredTemplates,
          },
          {
            id: AnalysisTypesAvailable.AdHoc,
            buttonProps: {text: 'Create new analysis'},
          },
        ]}
        wrap={true}
      />
    </div>
  );

  return (
    <Modal
      fullWidth={
        analysisType === AnalysisTypesAvailable.Preconfigured ||
        createAnalysisState.seedDataConfig?.type === SeedDataConfigType.WORKSHEET
      }
      title="Create analysis"
      onRequestSubmit={() => {
        Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL, analyticsModule, {
          ...analyticsMetadata,
          analysisType,
        });
        Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.SUBMITTED_CREATE_ANALYSIS, analyticsModule, {
          ...analyticsMetadata,
          analysisType,
        });
        handleRequestSubmit();
      }}
      onRequestClose={() => {
        Analytics.trackEventWithModule(
          COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL,
          analyticsModule,
          analyticsMetadata
        );
        props.onRequestClose();
      }}
      buttons={[{submit: true, text: 'Create', style: ButtonStyle.SECONDARY, disabled: disableSubmit}]}
    >
      {availableAnalysisTypes.length > 1 &&
        BenchlingConfig.PROCEDURES_ENABLE_ANALYSIS_TEMPLATE_INTEGRATION && (
          <>
            <FieldLabel label="Analysis option" />
            {analysisTypeToggle}
            {!analysisTemplateConfig.hasConfiguredTemplates && (
              <div className={css(styles.infoBanner)}>
                <InlineBanner
                  bannerType={InlineBannerType.INFO}
                  text="There are no pre-approved analyses configured for this unit operation"
                  supportingInfo="Contact your study administrator to set up pre-approved analysis templates."
                />
              </div>
            )}
          </>
        )}
      {analysisType === AnalysisTypesAvailable.AdHoc && (
        <CreateAnalysisForm
          createAnalysisState={createAnalysisState}
          setCreateAnalysisState={setCreateAnalysisState}
        />
      )}
      {analysisType === AnalysisTypesAvailable.Preconfigured &&
        props.pmei != null &&
        analyzePreconfiguredProcedurePane}
    </Modal>
  );
}

const styles = StyleSheet.create({
  infoBanner: {
    marginBottom: '16px',
  },
  analysisTypeToggleButtons: {
    marginBottom: '16px',
  },
});
